import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react';

import { GetMyPermissionsQuery, useGetMyPermissionsQuery } from '@/graphql';
import {
	PROVIDER_CACHE_KEYS,
	readProviderCache,
	readProviderCacheUpdatedAt,
	writeProviderCache,
} from '@/helpers/providerCache';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';

import { LoadingResource, useLoading } from '../LoadingProvider';

interface PermissionsContext {
	isLoading: boolean;
	permissions: string[];
	hasPermission: (action: string) => boolean;
	refetch: () => void;
}

const defaultValue: PermissionsContext = {
	isLoading: false,
	permissions: [],
	hasPermission: () => false,
	refetch: () => null,
};

const PermissionsContext = createContext<PermissionsContext>(defaultValue);

export function usePermissions(): PermissionsContext {
	const context = useContext(PermissionsContext);
	if (isNil(context)) {
		throw new Error(
			`usePermissions must be used within a PermissionsProvider`,
		);
	}

	return context;
}

export const PermissionsProvider: React.FC = ({ children }) => {
	const { setLoaded } = useLoading();
	const { enqueueSnackbar } = useSnackbar();
	const cachedPermissions = readProviderCache<GetMyPermissionsQuery>(
		PROVIDER_CACHE_KEYS.permissions,
	);
	const { data, isLoading, refetch, error } = useGetMyPermissionsQuery(
		{},
		{
			initialData: cachedPermissions ?? undefined,
			initialDataUpdatedAt: readProviderCacheUpdatedAt(
				PROVIDER_CACHE_KEYS.permissions,
			),
			refetchOnMount: true,
			refetchOnWindowFocus: true,
		},
	);
	const permissions = useMemo(() => data?.getMyPermissions || [], [data]);
	const hasPermission = useCallback(
		(action: string) => {
			return permissions.includes(action);
		},
		[permissions],
	);

	useEffect(() => {
		if (!isLoading) {
			setLoaded(LoadingResource.PERMISSIONS);
		}
	}, [isLoading, setLoaded]);

	useEffect(() => {
		if (data) {
			writeProviderCache(PROVIDER_CACHE_KEYS.permissions, data);
		}
	}, [data]);

	useEffect(() => {
		if (error && !data?.getMyPermissions?.length) {
			enqueueSnackbar('Failed to load permissions', {
				variant: 'warning',
			});
		}
	}, [data, enqueueSnackbar, error]);

	return (
		<PermissionsContext.Provider
			value={{
				isLoading,
				permissions,
				hasPermission,
				refetch,
			}}
		>
			{children}
		</PermissionsContext.Provider>
	);
};

export default PermissionsProvider;
